import Layout from "@/components/feature/Layout";
import SectionHeading from "@/components/base/SectionHeading";
import WorkCard from "@/components/base/WorkCard";
import ArticleCard from "@/components/base/ArticleCard";
import FAQAccordion from "@/components/base/FAQAccordion";
import Reveal from "@/components/base/Reveal";
import { useSeo } from "@/hooks/useSeo";
import { SITE } from "@/config/site";
import { works } from "@/mocks/works";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";
import { Link, useNavigate } from "react-router-dom";
import { scrollToTop } from "@/hooks/useScrollToTop";

const featured = works.filter((w) => w.featured).slice(0, 5);
const hot = [...works].sort((a, b) => b.rating - a.rating);
const latest = [...works].sort((a, b) => b.episodes - a.episodes);

const faqs = [
  { q: "抖漫是什么？", a: "抖漫是一个聚合短剧化动态漫画的平台，海量热门作品竖屏全屏在线看，自带配音与特效，一次满足你的动态漫观看需求。" },
  { q: "抖漫如何观看？", a: "所有作品均采用竖屏全屏呈现，点开即可观看，无需额外设置。支持电脑与手机浏览器直接访问观看。" },
  { q: "抖漫的动态漫画是免费的吗？", a: "抖漫提供大量免费作品，热门作品持续更新，让每一位用户都能轻松享受优质动态漫画内容。" },
  { q: "抖漫的作品更新频率如何？", a: "热门作品保持高频更新，点击进入作品后即可查看最新剧集，追更不迷路，不错过任何一集。" },
  { q: "如何找到适合自己的作品？", a: "你可以通过首页分类入口或导航栏的分类筛选功能，按玄幻仙侠、都市异能、甜宠恋爱等题材快速定位喜欢的内容。" },
  { q: "可以分享给朋友吗？", a: "当然可以！每一部作品和文章页面都支持分享到微博、抖音、微信等社交平台，好作品值得分享。" },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const heroJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE.name,
    url: SITE.baseUrl,
    slogan: SITE.slogan,
    description: SITE.description,
    contactPoint: {
      "@type": "ContactPoint",
      email: SITE.email,
      telephone: SITE.phone,
      contactType: "customer service",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE.name,
    url: SITE.baseUrl,
    description: SITE.description,
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: `${SITE.name} - 首页`,
    url: SITE.baseUrl,
    inLanguage: "zh-CN",
    isPartOf: { "@type": "WebSite", name: SITE.name, url: SITE.baseUrl },
  },
  faqJsonLd,
];

export default function Home() {
  useSeo({
    title: `抖漫 | 抖漫短剧_抖漫动态漫画_竖屏全屏有声特效在线看`,
    description: SITE.description,
    keywords: SITE.keywords,
    path: "/",
    jsonLd: heroJsonLd,
  });

  return (
    <Layout>
      {/* Hero */}
      <Hero />

      {/* Featured works */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-16">
        <Reveal>
          <SectionHeading title="热门推荐" desc="抖漫精选口碑爆款，竖屏全屏沉浸式观看" moreTo="/category/xuanhuan" />
          <div className="grid grid-cols-2 gap-4 md:gap-5 sm:grid-cols-3 lg:grid-cols-5">
            {featured.map((w) => (
              <WorkCard key={w.id} work={w} />
            ))}
          </div>
        </Reveal>
      </section>

      {/* Categories */}
      <section className="bg-background-900/60 border-y border-background-200/20 py-14 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <Reveal>
            <SectionHeading title="作品分类" desc="海量题材一网打尽，总有一部适合你" />
          </Reveal>
          <Reveal delay={120}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {categories.map((c) => (
                <Link
                  key={c.id}
                  to={`/category/${c.id}`}
                  onClick={scrollToTop}
                  className="group relative overflow-hidden rounded-xl aspect-[16/10]"
                >
                  <img
                    src={c.cover}
                    alt={`${c.name} 动态漫画分类`}
                    title={`${c.name} 抖漫分类`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background-950/95 via-background-950/30 to-transparent"></div>
                  <div className="absolute inset-x-0 bottom-0 p-4 md:p-5">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-7 h-7 rounded-md bg-primary-500 flex items-center justify-center text-background-950">
                        <i className={c.icon}></i>
                      </span>
                      <h3 className="font-heading text-lg md:text-xl text-foreground-50">{c.name}</h3>
                    </div>
                    <p className="text-xs text-foreground-300 line-clamp-1">{c.desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Latest updates + Ranking */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <Reveal>
              <SectionHeading title="最新更新" desc="热门作品持续更新，追更不迷路" moreTo="/category/dushi" />
            </Reveal>
            <Reveal delay={120}>
              <div className="grid grid-cols-2 gap-4 md:gap-5 sm:grid-cols-3">
                {latest.slice(0, 6).map((w) => (
                  <WorkCard key={w.id} work={w} />
                ))}
              </div>
            </Reveal>
          </div>

          <div>
            <Reveal>
              <SectionHeading title="热播榜" desc="抖漫实时人气排行" />
            </Reveal>
            <Reveal delay={120}>
              <div className="rounded-xl bg-background-900/70 border border-background-200/20 p-4 md:p-5">
                <ol className="space-y-3">
                  {hot.slice(0, 6).map((w, idx) => (
                    <li key={w.id}>
                      <Link
                        to={`/work/${w.id}`}
                        onClick={scrollToTop}
                        className="flex items-center gap-3 group"
                      >
                        <span
                          className={`w-7 h-7 shrink-0 rounded-md flex items-center justify-center text-sm font-bold ${
                            idx < 3 ? "bg-primary-500 text-background-950" : "bg-background-700 text-foreground-400"
                          }`}
                        >
                          {idx + 1}
                        </span>
                        <img
                          src={w.cover}
                          alt=""
                          className="w-10 rounded-md object-cover object-top shrink-0"
                          style={{ height: "52px" }}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-foreground-100 group-hover:text-primary-400 transition-colors whitespace-nowrap overflow-hidden text-ellipsis">
                            {w.title}
                          </p>
                          <div className="mt-0.5 flex items-center gap-2 text-xs text-foreground-600">
                            <span className="flex items-center gap-0.5 text-accent-400">
                              <i className="ri-star-fill"></i>
                              {w.rating.toFixed(1)}
                            </span>
                            <span>{w.categoryName}</span>
                          </div>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Feature highlights */}
      <Features />

      {/* Articles */}
      <section className="bg-background-900/60 border-y border-background-200/20 py-14 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <Reveal>
            <SectionHeading title="抖漫资讯" desc="行业观察、追更指南、作品推荐" moreTo="/articles" />
          </Reveal>
          <Reveal delay={120}>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              {articles.slice(0, 3).map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-16">
        <Reveal>
          <SectionHeading title="常见问题" desc="关于抖漫的疑问，这里为您解答" />
        </Reveal>
        <Reveal delay={120}>
          <FAQAccordion items={faqs} />
        </Reveal>
      </section>

      {/* CTA */}
      <CTASection />
    </Layout>
  );
}

function Hero() {
  const navigate = useNavigate();
  return (
    <section className="relative min-h-[560px] md:min-h-[640px] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Dark%20cinematic%20immersive%20streaming%20platform%20hero%20background%2C%20glowing%20vertical%20phone%20screens%20floating%20in%20dark%20space%20displaying%20colorful%20animated%20manhua%20artwork%2C%20dramatic%20warm%20orange%20and%20teal%20neon%20light%20rays%2C%20abstract%20digital%20entertainment%20atmosphere%2C%20high%20detail%2C%20cinematic%20lighting%2C%20modern%20streaming%20aesthetic&width=1600&height=900&seq=douman-hero-2026&orientation=landscape"
          alt="抖漫动态漫画平台"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-950/70 via-background-950/55 to-background-950"></div>
      </div>

      <div className="relative mx-auto max-w-7xl px-4 md:px-6 w-full py-20">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-900/60 backdrop-blur border border-primary-400/40 text-primary-300 text-xs md:text-sm mb-6">
            <i className="ri-play-circle-fill"></i>
            竖屏全屏 · 自带配音 · 特效沉浸
          </span>
          <h1 className="font-heading text-4xl md:text-6xl text-foreground-50 leading-tight mb-5">
            聚合短剧化<span className="text-primary-400">动态漫画</span>
            <br />
            点开即看 追更不迷路
          </h1>
          <p className="text-base md:text-lg text-foreground-300 leading-relaxed mb-8 max-w-xl">
            抖漫聚合超多热门动态漫画与短剧，竖屏全屏观看更沉浸，自带配音与特效。想看抖漫短剧、抖漫动态漫画，点开即看，一次满足你的动态漫观看需求。
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => {
                navigate("/category/xuanhuan");
                scrollToTop();
              }}
              className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-md bg-primary-500 text-background-950 text-base font-medium whitespace-nowrap hover:bg-primary-400 transition-colors cursor-pointer"
            >
              <i className="ri-play-fill text-xl"></i>
              立即观看
            </button>
            <button
              onClick={() => {
                navigate("/about");
                scrollToTop();
              }}
              className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-md bg-background-800/70 border border-background-300/40 text-foreground-100 text-base font-medium whitespace-nowrap hover:bg-background-700 transition-colors cursor-pointer"
            >
              了解抖漫
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Features() {
  const items = [
    { icon: "ri-smartphone-line", title: "竖屏全屏", desc: "专为手机竖屏优化，每一帧画面铺满屏幕，沉浸感拉满。" },
    { icon: "ri-mic-2-line", title: "自带配音", desc: "专业声优配音，让角色真正活起来，代入感十足。" },
    { icon: "ri-sparkling-2-line", title: "特效加持", desc: "动态特效与剧情完美结合，视觉体验全面升级。" },
    { icon: "ri-refresh-line", title: "追更不迷路", desc: "热门作品持续更新，智能提醒让你不错过每一集。" },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-16">
      <Reveal>
        <SectionHeading title="为什么选择抖漫" desc="打造更沉浸的动态漫画观看体验" />
      </Reveal>
      <Reveal delay={120}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {items.map((f, idx) => (
            <div
              key={idx}
              className="rounded-xl bg-background-900/70 border border-background-200/20 p-5 md:p-6"
            >
              <span className="w-11 h-11 rounded-lg bg-primary-500/15 flex items-center justify-center text-primary-400 text-xl mb-4">
                <i className={f.icon}></i>
              </span>
              <h3 className="text-foreground-100 font-medium mb-2">{f.title}</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </Reveal>
    </section>
  );
}

function CTASection() {
  return (
    <section className="mx-auto max-w-7xl px-4 md:px-6 pb-16 md:pb-20">
      <Reveal>
        <div className="relative overflow-hidden rounded-2xl bg-background-900 border border-background-200/20 px-6 py-12 md:px-12 md:py-16 text-center">
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-primary-500/20 blur-3xl"></div>
          <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-secondary-500/20 blur-3xl"></div>
          <div className="relative">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 mb-4">
              准备好开启你的<span className="text-primary-400">动态漫之旅</span>了吗？
            </h2>
            <p className="text-base text-foreground-400 max-w-xl mx-auto mb-8">
              海量热门作品持续更新，竖屏全屏有声特效，一次满足你的动态漫观看需求。
            </p>
            <Link
              to="/category/xuanhuan"
              onClick={scrollToTop}
              className="inline-flex items-center gap-2 px-8 py-3 rounded-md bg-primary-500 text-background-950 text-base font-medium whitespace-nowrap hover:bg-primary-400 transition-colors"
            >
              <i className="ri-play-fill text-xl"></i>
              立即开始观看
            </Link>
          </div>
        </div>
      </Reveal>
    </section>
  );
}